import { topUpdateContext } from '../lib/todayTopUpdate'
import { priceGroupSummary, priceMoney } from '../lib/todayPriceGroups'
import { todayAlertHeading } from '../lib/todayAlertHeading'
import { useAuth } from '@clerk/clerk-react'
import { TodayViewFilters } from '../components/shared/TodayViewFilters'
import { STANDARD_TODAY_VIEW, PRODUCT_TYPES, SORT_OPTIONS, matchesProductView, sameTodayView, type TodayView, type TodaySort } from '../lib/todayView'
import { TodayRefreshControl } from '../components/shared/TodayRefreshControl'
import { invalidateAlertCount } from '../hooks/useUnreviewedAlertCount'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAlerts, type Alert } from '../hooks/useAlerts'
import { useAuthFetch } from '../lib/useAuthFetch'
import { DecisionSummary, FeedItem, StatTile, Pill, DetailPanel, DetailSection, SectionLabel } from '../components/ui'
import type { FeedTone, FeedSeverity, PillTone } from '../components/ui'
import { AlertAssignModal } from '../components/shared/AlertAssignModal'
import { DeliveryChips } from '../components/shared/AssignControls'
import { useEligibleAssignees } from '../hooks/useEligibleAssignees'
import { assigneeLabel, summarizeDeliveries, overallDeliveryTone, namesFromAssignees, type DeliveryOutcome, type NotifyOption } from '../lib/taskAssign'
import {
  TODAY_SIGNAL_MAX_AGE_DAYS,
  isDecisionWorthyTodaySignal,
  todayImpactScore,
} from '../lib/todaySignals'

const API = import.meta.env.VITE_API_URL ?? ''

// ── Action Queue (s35) ───────────────────────────────────────────────────────
// Two real sources, no invented workflow:
//   1. TASKS — annotations filed against a SKU from Price Intelligence
//      (entity_type 'price_review'). These carry a real body and a real
//      assignee_id, and resolve via PATCH /annotations/:id/resolved.
//   2. ALERTS — the existing alert stream, reframed as actions.
// Tasks remain annotations, now with an explicit task kind, seven-day due date,
// completion timestamp and operator-recorded outcome. Each alert action IS a
// real alert the org already has:
//   • open  = alert.reviewed === false  (needs attention)
//   • done  = alert.reviewed === true   (resolved)
// "Mark complete" marks the underlying alert reviewed (existing PATCH endpoint); each
// action links to its real Alert Detail screen. Price-undercut, promo, catalog and
// block signals all flow through the alerts pipeline, so this queue surfaces them
// without a parallel (fabricated) task store.

// ── Helpers (shared with the Alert Feed s12) ─────────────────────────────────

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// Full timestamp in the user's local timezone (stored UTC, displayed local).
function localTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function ageDays(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000))
}

// Alerts still use the explicit seven-day review policy; assigned tasks carry
// their own persisted due date.
function reviewAgeLabel(iso: string): string {
  const days = ageDays(iso)
  return days >= 7 ? `OVERDUE · ${days}d` : timeAgo(iso)
}

function dueLabel(iso: string | null): string | null {
  if (!iso) return null
  const due = new Date(iso)
  const days = Math.ceil((due.getTime() - Date.now()) / 86400000)
  if (days < 0) return `OVERDUE · ${Math.abs(days)}d`
  if (days === 0) return 'DUE TODAY'
  if (days === 1) return 'DUE TOMORROW'
  return `DUE ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase()}`
}

const RESOLUTION_OPTIONS = [
  { code: 'acted', label: 'We acted', help: 'A pricing, promotion, or assortment change was made.' },
  { code: 'monitoring', label: 'We’re monitoring it', help: 'The evidence matters, but no change is needed yet.' },
  { code: 'dismissed', label: 'We dismissed it', help: 'The team reviewed the recommendation and chose not to act.' },
  { code: 'not_relevant', label: 'It wasn’t relevant', help: 'The recommendation did not apply to this location or decision.' },
] as const

type ResolutionCode = typeof RESOLUTION_OPTIONS[number]['code'] | 'completed_unspecified'

function resolutionLabel(code: ResolutionCode | null): string | null {
  if (!code) return null
  if (code === 'completed_unspecified') return 'Completed before outcome tracking'
  return RESOLUTION_OPTIONS.find(option => option.code === code)?.label ?? null
}

type Sev = FeedSeverity // 'critical' | 'high' | 'medium' | 'info'

function getSev(alert: Alert): Sev {
  if (alert.alert_type === 'price_drop') {
    if (alert.old_value && alert.new_value) {
      const pct = (parseFloat(alert.old_value) - parseFloat(alert.new_value)) / parseFloat(alert.old_value)
      if (pct >= 0.2) return 'critical'
      if (pct >= 0.1) return 'high'
    }
    return 'high'
  }
  if (alert.alert_type === 'new_promo') return 'high'
  if (alert.alert_type === 'competitor_blocked' || alert.alert_type === 'block_cancelled') return 'high'
  if (alert.alert_type === 'new_sku') return 'medium'
  return 'info'
}

const SEV_LABEL: Record<Sev, string> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', info: 'INFO' }
const SEV_PILL: Record<Sev, PillTone> = { critical: 'danger', high: 'danger', medium: 'warning', info: 'neutral' }

// Consistent facts in the operator-requested order, without instructions.
function getTitle(alert: Alert): string { return todayAlertHeading(alert).title }

// The one-line "what triggered this" detail.
function getSub(alert: Alert): string {
  if (alert.old_value && alert.new_value && (alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase'))
    return `$${alert.old_value} → $${alert.new_value}`
  if (alert.alert_type === 'new_promo') return 'New deal detected on their menu'
  if (alert.alert_type === 'new_sku') return 'Menu update detected'
  if (alert.alert_type === 'sku_removed') return 'Products removed from their menu'
  return alert.alert_type.replace(/_/g, ' ')
}

// A task = an annotation with an owner or a SKU behind it. Mirrors the shape
// GET /api/v1/annotations returns; nothing here is synthesized client-side.
interface TaskItem {
  id: string
  author_id: string | null
  entity_type: string | null
  entity_id: string | null
  body: string
  assignee_id: string | null
  resolved: boolean
  task_kind: 'note' | 'task'
  due_at: string | null
  resolved_at: string | null
  resolution_code: ResolutionCode | null
  created_at: string
  target_label: string | null
  target_kind: 'competitor' | 'location' | 'alert' | 'product' | null
  target_location_name?: string | null
  target_location_id?: string | null
  // Per-channel notification outcomes (present once migration 034 is applied).
  notifications?: DeliveryOutcome[]
}

function taskSourceHref(task: TaskItem): string | null {
  if (!task.entity_id || !task.entity_type) return null
  if (task.entity_type === 'alert') return `/alerts/${task.entity_id}`
  if (task.entity_type === 'competitor') return `/competitors/${task.entity_id}`
  if (task.entity_type === 'location') return `/today?location=${task.entity_id}`
  if (task.entity_type === 'price_review') {
    const query = new URLSearchParams()
    if (task.target_location_id) query.set('location_id', task.target_location_id)
    if (task.target_label) query.set('product', task.target_label)
    return `/prices${query.size ? `?${query.toString()}` : ''}`
  }
  return null
}

type ActionType = { label: string; pill: PillTone; tone: FeedTone; key: string }

function getActionType(alert: Alert): ActionType {
  switch (alert.alert_type) {
    case 'price_drop': return { label: 'Price undercut', pill: 'info', tone: 'danger', key: 'price' }
    case 'price_increase': return { label: 'Price move', pill: 'info', tone: 'warn', key: 'price' }
    case 'new_promo': return { label: 'Promo response', pill: 'warning', tone: 'warn', key: 'promo' }
    case 'new_sku':
    case 'sku_removed': return { label: 'Catalog change', pill: 'accent', tone: 'teal', key: 'catalog' }
    case 'competitor_blocked':
    case 'block_cancelled': return { label: 'Block action', pill: 'blocking', tone: 'rose', key: 'block' }
    default: return { label: alert.alert_type.replace(/_/g, ' '), pill: 'neutral', tone: 'neutral', key: 'other' }
  }
}

function ActionIcon({ type }: { type: string }) {
  if (type === 'price_drop' || type === 'price_increase')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>
  if (type === 'new_promo')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
  if (type === 'new_sku' || type === 'sku_removed')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>
  if (type === 'competitor_blocked' || type === 'block_cancelled')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
}

// ── Filter dropdown (Aurora+ chrome, matches Alert Feed) ─────────────────────
function Dropdown({
  label, active, open, onToggle, options, onPick,
}: {
  label: string
  active: boolean
  open: boolean
  onToggle: () => void
  options: { value: string; label: string }[]
  onPick: (value: string) => void
}) {
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); onToggle() }}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8,
          background: active ? 'var(--accent-soft)' : 'var(--surface)',
          border: `1px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
          color: active ? 'var(--accent)' : 'var(--text-2)',
          fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
        }}
      >
        {label}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 200,
          background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 10,
          boxShadow: 'var(--card-shadow)', minWidth: 170, padding: 4, maxHeight: 260, overflowY: 'auto',
        }}>
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={(e) => { e.stopPropagation(); onPick(opt.value) }}
              style={{
                display: 'flex', width: '100%', alignItems: 'center', gap: 7, padding: '7px 10px',
                borderRadius: 7, border: 'none', cursor: 'pointer', textAlign: 'left',
                background: opt.label === label ? 'var(--accent-soft)' : 'transparent',
                color: opt.label === label ? 'var(--accent)' : 'var(--text-1)',
                fontSize: 12, fontFamily: 'var(--sans)', fontWeight: opt.label === label ? 700 : 500,
              }}
              onMouseEnter={e => { if (opt.label !== label) (e.currentTarget as HTMLElement).style.background = 'var(--surface-3)' }}
              onMouseLeave={e => { if (opt.label !== label) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────────────────────────

type TabKey = 'open' | 'done' | 'all'

// Block-related alert types are excluded from Today entirely — their records
// are preserved untouched and remain discoverable in Alert History (/alerts).
const BLOCK_ALERT_TYPES = new Set(['competitor_blocked', 'block_cancelled'])

const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'price', label: 'Price undercut' },
  { value: 'promo', label: 'Promo response' },
  { value: 'catalog', label: 'Catalog change' },
]

export default function ActionQueue() {
  const { userId, orgId } = useAuth()
  return <ActionQueueView key={`${userId ?? 'loading'}:${orgId ?? 'personal'}`} />
}

function ActionQueueView() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<TabKey>('open')
  const [typeFilter, setTypeFilter] = useState<TodayView['actionType']>('all')
  const [brands, setBrands] = useState<string[]>([])
  const [productTypes, setProductTypes] = useState<string[]>([])
  const [sort, setSort] = useState<TodaySort>('impact')
  const [viewReady, setViewReady] = useState(false)
  const [savedView, setSavedView] = useState<TodayView | null>(null)
  const [viewSaving, setViewSaving] = useState(false)
  const [viewError, setViewError] = useState<string | null>(null)
  const [viewRetry, setViewRetry] = useState(0)
  const [locFilter, setLocFilter] = useState('all')
  const groupElements = useRef(new Map<string, HTMLElement>())
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [resolvingTask, setResolvingTask] = useState<TaskItem | null>(null)
  const [resolutionCode, setResolutionCode] = useState<Exclude<ResolutionCode, 'completed_unspecified'> | null>(null)
  const [resolutionSaving, setResolutionSaving] = useState(false)

  const [typeDdOpen, setTypeDdOpen] = useState(false)
  const [locDdOpen, setLocDdOpen] = useState(false)
  const filterBarRef = useRef<HTMLDivElement | null>(null)

  const [locations, setLocations] = useState<{ id: string; name: string; active?: boolean }[]>([])

  const selectedLocId = locFilter === 'all'
    ? null
    : locFilter

  // Filter and sort before paging. Counts cover every matching review state,
  // while rows follow the selected Open/Done/All tab.
  const { alerts, groups, hasMore, loading, loadingMore, error, total, counts, facets, loadMore, markReviewed, refetch: refetchAlerts } = useAlerts({
    locationId: selectedLocId ?? undefined,
    reviewed: activeTab === 'open' ? 'unreviewed' : activeTab === 'done' ? 'reviewed' : 'all',
    limit: 100, surface: 'today', brands, productTypes, sort, actionType: typeFilter, includeFacets: true, groupPrices: true, enabled: viewReady,
  })

  const loadLocations = useCallback(() => {
    authFetch(`${API}/api/v1/locations`)
      .then(r => r.json())
      .then(d => {
        const rows: { id: string; name: string }[] = d.locations || []
        setLocations(rows)
        // Apply ?location=<id> deep-link after locations load. If the id does
        // not match, preserve the saved/deep-linked scope until the user changes it.
        const deepLinkId = searchParams.get('location')
        if (deepLinkId) {
          const match = rows.find(l => l.id === deepLinkId)
          if (match) setLocFilter(match.id)
        }
      })
      .catch(() => setLocations([]))
  }, [authFetch, searchParams])

  useEffect(() => { loadLocations() }, [loadLocations])

  const applyView = (view: TodayView, locationOverride?: string | null) => {
    setBrands(view.brands); setProductTypes(view.productTypes); setSort(view.sort)
    setTypeFilter(view.actionType); setActiveTab(view.tab)
    setLocFilter(locationOverride || view.locationId || 'all'); setSelectedId(null)
  }
  useEffect(() => {
    let cancelled = false
    setViewReady(false); setViewError(null)
    authFetch(`${API}/api/v1/settings/today-view`).then(async response => {
      if (!response.ok) throw new Error('Your default view could not be loaded.')
      const data = await response.json()
      if (cancelled) return
      const view: TodayView | null = data.data?.view ?? null
      setSavedView(view); applyView(view ?? STANDARD_TODAY_VIEW, searchParams.get('location'))
      setViewReady(true)
    }).catch(() => { if (!cancelled) setViewError('Your default view could not be loaded. Retry to restore it.') })
    return () => { cancelled = true }
  }, [authFetch, viewRetry]) // Initial deep links override the saved location; later picks are live controls.

  const currentView: TodayView = { version: 1, brands, productTypes, sort, locationId: selectedLocId, actionType: typeFilter, tab: activeTab }
  const writeLocationUrl = (locationId: string | null) => {
    const next = new URLSearchParams(searchParams)
    locationId ? next.set('location', locationId) : next.delete('location')
    setSearchParams(next, { replace: true })
  }
  const saveDefault = async () => {
    setViewSaving(true); setViewError(null)
    try {
      const response = await authFetch(`${API}/api/v1/settings/today-view`, { method: 'PUT', body: JSON.stringify(currentView) })
      const result = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(result.error || 'Your default view could not be saved.')
      setSavedView(result.data.view); showToast('Default view saved for your account')
    } catch (error) { setViewError(error instanceof Error ? error.message : 'Your default view could not be saved.') }
    finally { setViewSaving(false) }
  }
  const resetDefault = async () => {
    setViewSaving(true); setViewError(null)
    try {
      const response = await authFetch(`${API}/api/v1/settings/today-view`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Your default view could not be reset.')
      setSavedView(null); applyView(STANDARD_TODAY_VIEW); writeLocationUrl(null); showToast('Default view reset')
    } catch (error) { setViewError(error instanceof Error ? error.message : 'Your default view could not be reset.') }
    finally { setViewSaving(false) }
  }

  // Close filter dropdowns on outside-click / Escape.
  useEffect(() => {
    if (!typeDdOpen && !locDdOpen) return
    const onMouseDown = (e: MouseEvent) => { if (!filterBarRef.current?.contains(e.target as Node)) { setTypeDdOpen(false); setLocDdOpen(false) } }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') { setTypeDdOpen(false); setLocDdOpen(false) } }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('mousedown', onMouseDown); document.removeEventListener('keydown', onKeyDown) }
  }, [typeDdOpen, locDdOpen])

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2600)
  }

  // ── Tasks (annotations filed from Price Intelligence) ─────────────────────
  const [assignTarget, setAssignTarget] = useState<Alert | null>(null)
  const [tasks, setTasks] = useState<TaskItem[]>([])
  const [tasksLoading, setTasksLoading] = useState(true)

  const loadTasks = useCallback(() => {
    setTasksLoading(true)
    authFetch(`${API}/api/v1/annotations?limit=200`)
      .then(r => r.json())
      .then(d => {
        const rows: TaskItem[] = d?.data?.annotations || []
        // A task is a note that someone is expected to DO something about:
        // it either names an assignee or was filed against a SKU from the
        // review queue. A bare comment on a rival stays a comment.
        setTasks(rows.filter(t => t.task_kind === 'task' || !!t.assignee_id || t.entity_type === 'price_review'))
      })
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false))
  }, [authFetch])

  useEffect(() => { loadTasks() }, [loadTasks])

  // Resolve assignee_id → member name. The eligible-assignees roster (all active
  // members, no location filter) is the id→name source; unresolved ids degrade to
  // a neutral "Assigned teammate" and legacy free-text is shown as-is. Never
  // exposes email/phone.
  const { assignees } = useEligibleAssignees(null)
  const memberNames = useMemo(() => namesFromAssignees(assignees), [assignees])

  const resolveTask = async (t: TaskItem, code?: Exclude<ResolutionCode, 'completed_unspecified'>, e?: React.MouseEvent) => {
    e?.stopPropagation()
    const next = !t.resolved
    if (next && !code) {
      setResolvingTask(t)
      setResolutionCode(null)
      return
    }
    setResolutionSaving(true)
    try {
      const r = await authFetch(`${API}/api/v1/annotations/${t.id}/resolved`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved: next, resolution_code: next ? code : null }),
      })
      if (!r.ok) { showToast("Couldn't update that task — try again"); return }
      const payload = await r.json().catch(() => null)
      const saved = payload?.data
      setTasks(prev => prev.map(x => (x.id === t.id ? {
        ...x,
        resolved: next,
        resolved_at: saved?.resolved_at ?? null,
        resolution_code: saved?.resolution_code ?? null,
      } : x)))
      setResolvingTask(null)
      setResolutionCode(null)
      showToast(next ? 'Outcome saved' : 'Task reopened')
    } catch {
      showToast("Couldn't reach the server — the task is unchanged")
    } finally {
      setResolutionSaving(false)
    }
  }

  // ── Computed ────────────────────────────────────────────────────────────────
  // Block-related alerts are excluded from all Today tabs and metrics.
  const typed = alerts
    .filter(a => !BLOCK_ALERT_TYPES.has(a.alert_type))
    // The API applies the same policy. Keep this client-side guard so mocked,
    // cached, or older responses can never put stale retail hardware on Today.
    .filter(a => isDecisionWorthyTodaySignal(a))
    .filter(a => typeFilter === 'all' || getActionType(a).key === typeFilter)
    .filter(a => matchesProductView(a, brands, productTypes))

  const openItems = typed.filter(a => !a.reviewed)
  const doneItems = typed.filter(a => a.reviewed)

  const visible = activeTab === 'open' ? openItems : activeTab === 'done' ? doneItems : typed

  const openTasks = tasks.filter(t => !t.resolved)
  const doneTasks = tasks.filter(t => t.resolved)

  // Summary metrics — all derived from loaded alerts and real tasks, never
  // fabricated. Tasks count as open actions because that is exactly what they
  // are: work somebody accepted and has not finished.
  const hasFocusedView = locFilter !== 'all' || brands.length > 0 || productTypes.length > 0
  const openCount = (counts?.open ?? openItems.length) + openTasks.length
  const weekAgo = Date.now() - 7 * 86400000
  const resolvedWeek = (counts?.doneWeek ?? doneItems.filter(a => a.reviewed_at && new Date(a.reviewed_at).getTime() >= weekAgo).length)
    + doneTasks.filter(t => t.resolved_at && new Date(t.resolved_at).getTime() >= weekAgo).length

  const locLabel = locFilter === 'all' ? 'All locations' : locations.find(location => location.id === locFilter)?.name ?? 'Unavailable location'
  const typeLabel = TYPE_OPTIONS.find(t => t.value === typeFilter)?.label ?? 'All types'

  // Sort open alerts by observed impact rather than arrival order. The score is
  // a transparent triage proxy; it never claims sales or margin exposure.
  const byNewest = (a: Alert, b: Alert) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  const byImpact = (a: Alert, b: Alert) => todayImpactScore(b) - todayImpactScore(a) || byNewest(a, b)
  const displayOrder = sort === 'impact' ? byImpact : sort === 'newest' ? byNewest : () => 0
  const prioritizedOpen = [...openItems].sort(displayOrder)
  const highBand = prioritizedOpen.filter(a => { const s = getSev(a); return s === 'critical' || s === 'high' })
  const normalBand = prioritizedOpen.filter(a => { const s = getSev(a); return s === 'medium' || s === 'info' })
  const topPriority = [...openItems].sort(byImpact)[0] ?? null
  const topGroup = topPriority ? groups?.find(group => group.alerts.some(alert => alert.id === topPriority.id)) : undefined
  const openIds = new Set(openItems.map(alert => alert.id))
  const topContext = topPriority ? topUpdateContext(topPriority, topGroup ? { ...topGroup, alerts: topGroup.alerts.filter(alert => openIds.has(alert.id)) } : undefined) : null
  const selected = visible.find(a => a.id === selectedId)
    ?? [...openItems, ...doneItems].find(a => a.id === selectedId)
    ?? prioritizedOpen[0]
    ?? doneItems[0]
    ?? null

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDone = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    try {
      await markReviewed(id)
      showToast('Marked as done')
    } catch {
      showToast("Couldn't mark this done — please try again")
    }
  }

  const submitAssignment = async (alert: Alert, opts: { assigneeUserId: string; assigneeName: string; notify: NotifyOption; note: string }) => {
    const response = await authFetch(`${API}/api/v1/annotations`, {
      method: 'POST',
      body: JSON.stringify({
        entity_type: 'alert', entity_id: alert.id, task_kind: 'task',
        body: opts.note.trim() || `Follow up on ${getTitle(alert)}`,
        assignee_id: opts.assigneeUserId, notify: opts.notify,
      }),
    })
    const result = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(result.error || 'Could not assign this alert. Try again.')
    setAssignTarget(null)
    setActiveTab('open')
    loadTasks()
    const delivery = summarizeDeliveries(result?.data?.deliveries)
    showToast(`Assigned to ${opts.assigneeName}${delivery ? ` · ${delivery}` : ''}`)
  }

  const visibleTasks = activeTab === 'open' ? openTasks : activeTab === 'done' ? doneTasks : tasks

  // ── Render a task card ──────────────────────────────────────────────────────
  // Tasks read differently to alerts: the body IS the instruction, so it is the
  // title. The chips carry who owns it and which SKU/store it came from.
  function renderTask(t: TaskItem) {
    const sourceHref = taskSourceHref(t)
    const outcome = resolutionLabel(t.resolution_code)
    const due = dueLabel(t.due_at)
    return (
      <FeedItem
        key={t.id}
        variant="card"
        severity={t.resolved ? 'info' : 'high'}
        tone="teal"
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>}
        title={t.body}
        time={t.resolved && t.resolved_at ? `DONE · ${timeAgo(t.resolved_at)}` : (due ?? reviewAgeLabel(t.created_at))}
        sub={`${t.target_label ? `On ${t.target_label}` : 'Task'} · Created ${localTimestamp(t.created_at)}`}
        unread={!t.resolved}
        reviewed={t.resolved}
        chips={(() => {
          const who = assigneeLabel(t.assignee_id, memberNames)
          const deliverySummary = summarizeDeliveries(t.notifications)
          return (
          <>
            <Pill variant="chip" tone="accent">Task</Pill>
            {who
              ? <Pill variant="chip" tone="warning">{`Assigned · ${who}`}</Pill>
              : <Pill variant="chip" tone="neutral">Unassigned</Pill>}
            {deliverySummary && <DeliveryChips summary={deliverySummary} tone={overallDeliveryTone(t.notifications)} />}
            {t.target_location_name && <Pill variant="chip" tone="neutral">{t.target_location_name}</Pill>}
            {!t.resolved && due && <Pill variant="chip" tone={due.startsWith('OVERDUE') ? 'danger' : 'warning'}>{due}</Pill>}
            {t.resolved && outcome && <Pill variant="chip" tone="accent">{outcome}</Pill>}
            {sourceHref && (
              <button
                data-testid="task-view-evidence"
                onClick={(e) => { e.stopPropagation(); navigate(sourceHref) }}
                style={{ border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, cursor: 'pointer', padding: '2px 4px' }}
              >
                View evidence →
              </button>
            )}
            {t.resolved ? (
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="9" height="9"><polyline points="20 6 9 17 4 12" /></svg>
                Done
              </span>
            ) : (
              <button
                data-testid="task-mark-done"
                onClick={(e) => { e.stopPropagation(); void resolveTask(t) }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 5,
                  border: '1px solid var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent)',
                  fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="9" height="9"><polyline points="20 6 9 17 4 12" /></svg>
                Mark done
              </button>
            )}
          </>
          )
        })()}
      />
    )
  }

  // ── Render an action card ───────────────────────────────────────────────────
  function renderCard(alert: Alert, compact = false) {
    const heading = todayAlertHeading(alert)
    const at = getActionType(alert)
    const sev = getSev(alert)
    const isDone = alert.reviewed
    const chips = (
      <>
        {!compact && <Pill variant="chip" tone={at.pill}>{at.label}</Pill>}
        {!compact && !isDone && <Pill variant="chip" tone={SEV_PILL[sev]}>{SEV_LABEL[sev]}</Pill>}
        {!compact && <Pill variant="chip" tone="identity">{alert.competitor_name}</Pill>}
        {!compact && alert.location_name && <Pill variant="chip" tone="neutral">{alert.location_name}</Pill>}
        {compact && <button type="button" className="btn" style={{ padding: '2px 8px', fontSize: 10 }} onClick={event => { event.stopPropagation(); setSelectedId(alert.id) }}>View evidence</button>}
        <button
          data-testid="today-alert-assign"
          onClick={event => { event.stopPropagation(); setSelectedId(alert.id); setAssignTarget(alert) }}
          style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 5,
            border: '1px solid var(--accent-hover)', background: 'var(--accent-hover)', color: 'var(--cta-fg-on-pos)',
            fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}
        >Assign</button>
        {isDone ? (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="9" height="9"><polyline points="20 6 9 17 4 12" /></svg>
            Resolved{alert.reviewed_at ? ` · ${timeAgo(alert.reviewed_at)}` : ''}
          </span>
        ) : (
          <button
            onClick={(e) => handleDone(alert.id, e)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 5,
              border: '1px solid var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent)',
              fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="9" height="9"><polyline points="20 6 9 17 4 12" /></svg>
            Mark complete
          </button>
        )}
      </>
    )
    return (
      <FeedItem
        key={alert.id}
        variant="card"
        severity={compact ? 'info' : sev}
        tone={at.tone}
        icon={compact ? undefined : <ActionIcon type={alert.alert_type} />}
        title={compact ? <span style={{ fontSize: 13, fontWeight: 600 }}>{alert.entity_name || 'Product'}</span> : <span data-testid="today-alert-heading" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 16, fontWeight: 750 }}>{heading.what}</span>
          <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45 }}>{[heading.brand, heading.dispensary].filter(Boolean).join(' · ')}</span>
          {(heading.category || heading.subcategory) && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-2)' }}>{[heading.category, heading.subcategory].filter(Boolean).join(' · ')}</span>}
        </span>}
        time={isDone ? timeAgo(alert.created_at) : reviewAgeLabel(alert.created_at)}
        sub={<span>{(alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase') && alert.old_value && alert.new_value
          ? <strong data-testid="today-card-prices" style={{ fontFamily: 'var(--sans)', fontSize: compact ? 16 : 13, fontWeight: 750, color: 'var(--text-1)' }}>{`${priceMoney(alert.old_value)} → ${priceMoney(alert.new_value)}`}</strong>
          : getSub(alert)}{!compact && alert.entity_name && <span style={{ display: 'block', marginTop: 4, color: 'var(--text-2)', fontSize: 11 }}>{alert.entity_name}</span>}</span>}
        chips={chips}
        unread={!isDone}
        reviewed={isDone}
        selected={selectedId === alert.id}
        onClick={() => setSelectedId(alert.id)}
      />
    )
  }

  function renderUpdates(items: Alert[]) {
    if (!groups) return items.map(alert => renderCard(alert))
    const visibleIds = new Set(items.map(alert => alert.id))
    const byAlert = new Map(groups.flatMap(group => group.alerts.map(alert => [alert.id, group] as const)))
    const rendered = new Set<string>()
    return items.flatMap(alert => {
      const group = byAlert.get(alert.id)
      if (!group) return [renderCard(alert)]
      if (rendered.has(group.id)) return []
      rendered.add(group.id)
      const members = group.alerts.filter(member => visibleIds.has(member.id))
      if (members.length < 2) return members.map(member => renderCard(member))
      const summary = priceGroupSummary(members)
      if (!summary) return members.map(member => renderCard(member))
      const first = members[0]
      const at = getActionType(first)
      return [<article ref={node => { if (node) groupElements.current.set(group.id, node); else groupElements.current.delete(group.id) }} key={group.id} data-testid="today-price-group" style={{ marginBottom: 12 }}>
        <FeedItem
          variant="card" severity={getSev(first)} tone={at.tone}
          unread={!first.reviewed} reviewed={first.reviewed} style={{ marginBottom: 0 }}
          icon={<span data-testid="today-group-icon" aria-hidden="true" style={{ display: 'inline-flex' }}><ActionIcon type={first.alert_type} /></span>}
          title={<span data-testid="today-group-event-heading" style={{ fontSize: 16, fontWeight: 750 }}>{todayAlertHeading(first).what}</span>}
          time={<time dateTime={first.created_at} title={localTimestamp(first.created_at)}>{timeAgo(first.created_at)}</time>}
          sub={<h3 style={{ margin: 0, fontFamily: 'var(--sans)', fontSize: 16, fontWeight: 500, lineHeight: 1.55, color: 'var(--text-1)' }}>
            <strong data-testid="today-group-dispensary" style={{ fontSize: 'inherit', fontWeight: 750 }}>{first.competitor_name}</strong>{' '}{summary.description}
            <strong data-testid="today-group-old-price" style={{ fontSize: 'inherit', fontWeight: 750 }}>{priceMoney(first.old_value)}</strong>{' to '}
            <strong data-testid="today-group-new-price" style={{ fontSize: 'inherit', fontWeight: 750 }}>{priceMoney(first.new_value)}</strong>.
          </h3>}
          chips={<>
            <Pill variant="chip" tone={at.pill}>{at.label}</Pill>
            <Pill variant="chip" tone={first.reviewed ? 'neutral' : SEV_PILL[getSev(first)]}>{first.reviewed ? 'Resolved' : SEV_LABEL[getSev(first)]}</Pill>
            <Pill variant="chip" tone="identity">{first.competitor_name}</Pill>
            {first.location_name && <Pill variant="chip" tone="neutral">{first.location_name}</Pill>}
          </>}
          footer={
        <details open={expandedGroups.has(group.id)} onToggle={event => {
          const open = event.currentTarget.open
          setExpandedGroups(previous => {
            if (previous.has(group.id) === open) return previous
            const next = new Set(previous)
            open ? next.add(group.id) : next.delete(group.id)
            return next
          })
        }}>
          <summary style={{ cursor: 'pointer', color: 'var(--accent-hover)', fontSize: 13, fontWeight: 650, padding: '4px 0' }}>View {members.length} {summary.noun}</summary>
          <div style={{ paddingTop: 12 }} data-testid="today-group-members">{expandedGroups.has(group.id) && members.map(member => renderCard(member, true))}</div>
        </details>
          }
        />
      </article>]
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg)' }}>
      {/* ── Topbar ──────────────────────────────────────────────────────────── */}
      <div style={{
        padding: '16px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 400, color: 'var(--text-1)', letterSpacing: '-0.015em', lineHeight: 1, margin: 0 }}>
            Today
          </h1>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 5 }}>
            Intelligence for today · Review · Act
          </div>
        </div>
        <span data-testid="action-queue-order" style={{
          padding: '5px 9px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--surface-2)',
          color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>Fresh · last {TODAY_SIGNAL_MAX_AGE_DAYS} days</span>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────────── */}
      <div ref={filterBarRef} style={{
        padding: '8px 28px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Type</span>
        <Dropdown
          label={typeLabel}
          active={typeFilter !== 'all'}
          open={typeDdOpen}
          onToggle={() => { setTypeDdOpen(o => !o); setLocDdOpen(false) }}
          options={TYPE_OPTIONS}
          onPick={(v) => { setTypeFilter(v as TodayView['actionType']); setSelectedId(null); setTypeDdOpen(false) }}
        />
        <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 2px' }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
        <Dropdown
          label={locLabel}
          active={locFilter !== 'all'}
          open={locDdOpen}
          onToggle={() => { setLocDdOpen(o => !o); setTypeDdOpen(false) }}
          options={[{ value: 'all', label: 'All locations' }, ...locations.map(l => ({ value: l.id, label: l.name }))]}
          onPick={(v) => { setLocFilter(v); writeLocationUrl(v === 'all' ? null : v); setSelectedId(null); setLocDdOpen(false) }}
        />
        <div style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
          {hasFocusedView ? `${openCount} matching updates · ${resolvedWeek} resolved this week` : 'Filter by location, brand, or product type'}
        </div>
      </div>

      <TodayViewFilters brands={brands} productTypes={productTypes} sort={sort}
        brandOptions={facets?.brands ?? []} typeValues={facets?.productTypes.map(type => type.value) ?? []}
        onBrands={values => { setBrands(values); setSelectedId(null) }} onTypes={values => { setProductTypes(values); setSelectedId(null) }} onSort={setSort}
        onSave={() => { void saveDefault() }} onReset={() => { void resetDefault() }}
        onRestore={() => { if (savedView) { applyView(savedView); writeLocationUrl(savedView.locationId) } }}
        saving={viewSaving} ready={viewReady && hasFocusedView} saved={!!savedView} changed={!!savedView && !sameTodayView(currentView, savedView)} />
      {viewError && <div role="alert" style={{ padding: '8px 28px', color: 'var(--danger)', fontSize: 12 }}>
        {viewError} {!viewReady && <button onClick={() => setViewRetry(value => value + 1)}>Retry</button>}
      </div>}

      {/* ── Layout ──────────────────────────────────────────────────────────── */}
      {viewReady && !hasFocusedView ? (
        <section data-testid="today-choose-focus" aria-labelledby="today-focus-title" style={{ flex: 1, overflowY: 'auto', padding: '48px 28px' }}>
          <div style={{ maxWidth: 580, margin: '24px auto', padding: 28, border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)' }}>
            <h2 id="today-focus-title" style={{ margin: '0 0 12px', fontFamily: 'var(--display)', fontSize: 28, color: 'var(--text-1)' }}>What do you want to follow?</h2>
            <p style={{ margin: '0 0 20px', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.65 }}>Choose a location, brand, or product type above to see relevant updates. Combine filters to narrow your view.</p>
            <button className="btn btn-primary" onClick={() => { setLocDdOpen(true); setTypeDdOpen(false) }}>Choose a location</button>
            <p style={{ margin: '18px 0 0', color: 'var(--text-3)', fontSize: 12, lineHeight: 1.6 }}>Use Save as default to open your chosen view automatically next time.</p>
          </div>
        </section>
      ) : (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Queue list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', minHeight: 0 }}>
          {activeTab === 'open' && topPriority && (
            <DecisionSummary
              className="action-queue-summary"
              question="What should I do next?"
              priority={getSev(topPriority) === 'critical' ? 'critical' : 'high'}
              answer={topContext!.title}
              action={(
                <button
                  data-testid="action-queue-review-priority"
                  onClick={() => {
                    if (topContext?.groupId) {
                      const id = topContext.groupId
                      setExpandedGroups(previous => new Set(previous).add(id))
                      setSelectedId(topPriority.id)
                      requestAnimationFrame(() => groupElements.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
                    } else navigate(`/alerts/${topPriority.id}`)
                  }}
                  style={{
                    padding: '9px 14px', borderRadius: 9, border: '1px solid var(--accent)',
                    background: 'var(--accent)', color: '#fff', cursor: 'pointer',
                    fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700,
                  }}
                >
                  {topContext!.actionLabel}
                </button>
              )}
            >
              {(topContext?.product || topContext?.prices) && <div data-testid="today-top-product-context" style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0', color: 'var(--text-1)' }}>
                {topContext.product && <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>{topContext.product}</span>}
                {topContext.prices && <strong style={{ fontSize: 16, fontWeight: 750 }}>{topContext.prices}</strong>}
              </div>}
              <TodayRefreshControl
                locationIds={locations.filter(l => l.active !== false && (!selectedLocId || l.id === selectedLocId)).map(l => l.id)}
                onUpdated={() => { void refetchAlerts(); invalidateAlertCount() }}
              />
            </DecisionSummary>
          )}
          {!(activeTab === 'open' && topPriority) && (
            <TodayRefreshControl
              locationIds={locations.filter(l => l.active !== false && (!selectedLocId || l.id === selectedLocId)).map(l => l.id)}
              onUpdated={() => { void refetchAlerts(); invalidateAlertCount() }}
            />
          )}

          {/* Status tabs */}
          <div style={{ display: 'flex', gap: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: 3, margin: activeTab === 'open' && topPriority ? '14px 0 16px' : '0 0 16px', width: 'fit-content' }}>
            {([['open', 'Open', (counts?.open ?? openItems.length) + openTasks.length], ['done', 'Done', (counts?.done ?? doneItems.length) + doneTasks.length], ['all', 'All', (counts ? counts.open + counts.done : typed.length) + tasks.length]] as [TabKey, string, number][]).map(([key, label, count]) => (
              <div
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, transition: 'var(--transition)',
                  background: activeTab === key ? 'var(--accent)' : 'transparent',
                  color: activeTab === key ? '#fff' : 'var(--text-3)',
                }}
              >
                {label}
                {hasFocusedView && <span style={{
                  fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                  background: activeTab === key ? 'rgba(255,255,255,0.25)' : 'var(--surface-3)',
                  color: activeTab === key ? '#fff' : 'var(--text-3)',
                }}>{count}</span>}
              </div>
            ))}
          </div>

          {/* States */}
          {loading || tasksLoading ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
              Pulling latest intelligence…
            </div>
          ) : error && !alerts.length ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>Couldn't load your action queue</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>We'll retry shortly. Check your connection and refresh.</div>
            </div>
          ) : visible.length === 0 && visibleTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" width="32" height="32" style={{ marginBottom: 12, opacity: 0.5 }}><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>
                {brands.length || productTypes.length || typeFilter !== 'all' ? 'No alerts match this view' : activeTab === 'done' ? 'Nothing resolved yet' : `All clear across ${locLabel === 'All locations' ? 'your markets' : locLabel}`}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                {brands.length || productTypes.length ? 'Choose other brands or product types to broaden this view.' : activeTab === 'done'
                  ? 'Resolved actions will appear here once you clear them.'
                  : `No decision-worthy changes in the last ${TODAY_SIGNAL_MAX_AGE_DAYS} days. Older signals remain in Alert History.`}
              </div>
            </div>
          ) : activeTab === 'open' ? (
            <>
              {visibleTasks.length > 0 && (
                <div data-testid="task-band">
                  <SectionLabel style={{ margin: '4px 0 8px' }}>Teammate-assigned tasks</SectionLabel>
                  {visibleTasks.map(renderTask)}
                </div>
              )}
              {sort !== 'impact' && <><SectionLabel>{SORT_OPTIONS.find(option => option.value === sort)?.label}</SectionLabel>{renderUpdates(prioritizedOpen)}</>}
              {sort === 'impact' && highBand.length > 0 && (
                <>
                  <SectionLabel style={{ margin: '4px 0 8px' }}>Fresh recommendations · impact-ranked</SectionLabel>
                  {renderUpdates(highBand)}
                </>
              )}
              {sort === 'impact' && normalBand.length > 0 && (
                <>
                  <SectionLabel style={{ margin: '16px 0 8px' }}>Monitor next</SectionLabel>
                  {renderUpdates(normalBand)}
                </>
              )}
            </>
          ) : (
            <>
              {visibleTasks.length > 0 && (
                <div data-testid="task-band">
                  <SectionLabel style={{ margin: '4px 0 8px' }}>{activeTab === 'done' ? 'Completed tasks' : 'Assigned tasks'}</SectionLabel>
                  {visibleTasks.map(renderTask)}
                </div>
              )}
              <SectionLabel style={{ margin: '16px 0 8px' }}>{activeTab === 'done' ? 'Resolved alerts' : 'All alerts'}</SectionLabel>
              {renderUpdates([...visible].sort(displayOrder))}
            </>
          )}
          {error && alerts.length > 0 && <p role="alert">{error}</p>}
          {hasMore && !loading && (
            <button data-testid="today-load-more" disabled={loadingMore} onClick={() => { void loadMore() }} className="btn" style={{ margin: '16px 0' }}>
              {loadingMore ? 'Loading…' : hasFocusedView ? `Load more updates (${groups ? groups.length : alerts.length} of ${total})` : 'Load more updates'}
            </button>
          )}
        </div>

        {/* Detail panel */}
        <div style={{ width: 372, flexShrink: 0, background: 'var(--surface)', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selected ? (
            <DetailContent
              alert={selected}
              onDone={() => handleDone(selected.id)}
              onAssign={() => setAssignTarget(selected)}
              onViewAlert={() => navigate(`/alerts/${selected.id}`)}
            />
          ) : (
            <div style={{ padding: '32px 20px', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
              Select an action to see its detail.
            </div>
          )}
        </div>
      </div>

      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)', border: '1px solid var(--border-2)',
          borderRadius: 'var(--r-sm)', padding: '11px 18px', fontSize: 12, fontWeight: 600, color: 'var(--text-1)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 9000, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
          {toast}
        </div>
      )}

      {assignTarget && (
        <AlertAssignModal key={assignTarget.id} alert={assignTarget} title={getTitle(assignTarget)}
          onClose={() => setAssignTarget(null)} onSubmit={submitAssignment} />
      )}

      {resolvingTask && (
        <div
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !resolutionSaving) setResolvingTask(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(9,18,28,0.52)', display: 'grid', placeItems: 'center', padding: 20 }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="task-outcome-title" style={{ width: 'min(520px, 100%)', background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 14, boxShadow: '0 24px 70px rgba(0,0,0,0.28)', padding: 22 }}>
            <div id="task-outcome-title" style={{ fontFamily: 'var(--display)', fontSize: 22, color: 'var(--text-1)', marginBottom: 6 }}>How did this task end?</div>
            <div style={{ color: 'var(--text-2)', fontSize: 12, lineHeight: 1.55, marginBottom: 16 }}>{resolvingTask.body}</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {RESOLUTION_OPTIONS.map(option => (
                <button
                  key={option.code}
                  data-testid={`task-outcome-${option.code}`}
                  onClick={() => setResolutionCode(option.code)}
                  style={{ textAlign: 'left', padding: '11px 13px', borderRadius: 9, border: `1px solid ${resolutionCode === option.code ? 'var(--accent)' : 'var(--border)'}`, background: resolutionCode === option.code ? 'var(--accent-soft)' : 'var(--surface-2)', color: 'var(--text-1)', cursor: 'pointer' }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{option.label}</div>
                  <div style={{ marginTop: 2, fontSize: 10.5, color: 'var(--text-3)', lineHeight: 1.45 }}>{option.help}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button disabled={resolutionSaving} onClick={() => setResolvingTask(null)} style={{ padding: '8px 13px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer' }}>Cancel</button>
              <button
                data-testid="task-save-outcome"
                disabled={!resolutionCode || resolutionSaving}
                onClick={() => { if (resolutionCode) void resolveTask(resolvingTask, resolutionCode) }}
                style={{ padding: '8px 13px', borderRadius: 8, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: resolutionCode && !resolutionSaving ? 'pointer' : 'not-allowed', opacity: resolutionCode && !resolutionSaving ? 1 : 0.55 }}
              >
                {resolutionSaving ? 'Saving…' : 'Save outcome'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Detail panel content ─────────────────────────────────────────────────────
function DetailContent({ alert, onDone, onViewAlert, onAssign }: { alert: Alert; onDone: () => void; onViewAlert: () => void; onAssign: () => void }) {
  const at = getActionType(alert)
  const sev = getSev(alert)
  const isDone = alert.reviewed

  const isPrice = (alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase') && alert.old_value && alert.new_value
  const oldV = alert.old_value ? parseFloat(alert.old_value) : null
  const newV = alert.new_value ? parseFloat(alert.new_value) : null
  const delta = oldV != null && newV != null ? newV - oldV : null
  const pct = oldV != null && newV != null && oldV !== 0 ? Math.round((delta! / oldV) * 100) : null

  return (
    <>
      {/* Header */}
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
        <DetailPanel
          eyebrow={[alert.competitor_name, alert.location_name].filter(Boolean).join(' · ').toUpperCase()}
          title={getTitle(alert)}
        >
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: -4 }}>
            <Pill variant="chip" tone={at.pill}>{at.label}</Pill>
            <Pill variant="chip" tone={SEV_PILL[sev]}>{SEV_LABEL[sev]}</Pill>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: isDone ? 'var(--accent)' : 'var(--text-3)' }}>
              {isDone && alert.reviewed_at ? `Resolved ${localTimestamp(alert.reviewed_at)}` : reviewAgeLabel(alert.created_at)}
            </span>
          </div>
        </DetailPanel>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        {isPrice && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
            <StatTile size="sm" value={priceMoney(alert.old_value)} label="Was" />
            <StatTile size="sm" tone={alert.alert_type === 'price_drop' ? 'danger' : 'warm'} value={priceMoney(alert.new_value)} label="Now" />
            <StatTile size="sm" tone={alert.alert_type === 'price_drop' ? 'danger' : 'warm'} value={`${pct! > 0 ? '+' : ''}${pct}%`} label="Change" />
          </div>
        )}

        <DetailSection label="Source evidence">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: at.tone === 'danger' ? 'var(--danger)' : at.tone === 'warn' ? 'var(--warm)' : at.tone === 'rose' ? 'var(--rose)' : 'var(--accent)', flexShrink: 0 }}>
              <ActionIcon type={alert.alert_type} />
            </span>
            <span>{getSub(alert)}</span>
          </div>
          <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
            Alert fired {localTimestamp(alert.created_at)}
            {alert.location_name ? ` · ${alert.location_name}` : ''}
          </div>
        </DetailSection>

        {(alert.entity_name || alert.entity_brand || alert.entity_category) && (
          <DetailSection label="Product">
            <div style={{ fontWeight: 600 }}>{alert.entity_name || alert.entity_brand}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-2)', marginTop: 3 }}>
              {[alert.entity_brand && alert.entity_name ? alert.entity_brand : null, alert.entity_category, alert.entity_package_size].filter(Boolean).join(' · ')}
            </div>
          </DetailSection>
        )}

        {isDone && (
          <DetailSection variant="recommendation-muted" label="Resolved">
            This action was marked done. It stays here as a record of what you resolved.
          </DetailSection>
        )}

      </div>

      {/* Footer */}
      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          onClick={onViewAlert}
          style={{
            flex: '1 0 100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px 13px',
            borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)',
            border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-2)' }}
        >
          Review source alert
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        </button>
        <button data-testid="today-detail-assign" onClick={onAssign}
          style={{ flex: 1, padding: '9px 13px', borderRadius: 10, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--sans)', border: '1px solid var(--border-2)',
            background: 'var(--surface-2)', color: 'var(--text-2)' }}>Assign</button>
        {!isDone && (
          <button
            onClick={onDone}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px 13px',
              borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)',
              border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text-2)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
            Mark complete
          </button>
        )}
      </div>
    </>
  )
}
